import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { StickyBanner } from "@/components/StickyBanner";
import { SEO } from "@/components/SEO";
import { SpanishHero } from "@/components/SpanishHero";
import { SpanishWhyUs } from "@/components/SpanishWhyUs";
import { SpanishServices } from "@/components/SpanishServices";
import { SpanishTestimonials } from "@/components/SpanishTestimonials";
import { SpanishFAQ } from "@/components/SpanishFAQ";
import { SpanishFinalCTA } from "@/components/SpanishFinalCTA";
import { LocationContext } from "@/lib/locations";

import { Guarantee } from "@/components/Guarantee";
import { PricingCalculator } from "@/components/PricingCalculator";
import { FrequencyDiscounts } from "@/components/FrequencyDiscounts";
import { InternalLinks } from "@/components/InternalLinks";
import { MapPin, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useScrollAnimation } from "@/hooks/use-scroll-animation";
import { cn } from "@/lib/utils";

const BOOKING_URL = "/book";

const NearbyAreas = () => {
  const { ref, isVisible } = useScrollAnimation();
  const areas = [
    { zone: "San José", cities: "Willow Glen, Almaden Valley, Evergreen, Berryessa" },
    { zone: "Santa Clara", cities: "Santa Clara, Sunnyvale, Cupertino, Mountain View" },
    { zone: "Palo Alto", cities: "Palo Alto, Los Altos, Menlo Park, East Palo Alto" },
    { zone: "Milpitas & Gilroy", cities: "Milpitas, Morgan Hill, Gilroy, Los Gatos" },
  ];
  return (
    <section className="py-16 bg-muted/30">
      <div className="container">
        <div ref={ref} className={cn("text-center mb-10 anim-fade-up", isVisible && "is-visible")}>
          <h2 className="text-2xl md:text-3xl font-bold mb-3">Áreas que Servimos en el Condado de Santa Clara</h2>
          <p className="text-muted-foreground">Limpieza profesional a domicilio en todo Silicon Valley y sus alrededores.</p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {areas.map((area, i) => (
            <div
              key={i}
              className={cn("bg-background rounded-2xl p-6 border shadow-sm hover:shadow-md hover:-translate-y-1 transition-all duration-300 text-center anim-fade-up", isVisible && "is-visible")}
              style={{ transitionDelay: `${i * 100}ms` }}
            >
              <MapPin className="h-6 w-6 text-primary mx-auto mb-3" />
              <h3 className="font-bold text-primary mb-2">{area.zone}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{area.cities}</p>
            </div>
          ))}
        </div>
        <div className="mt-10 text-center flex flex-col sm:flex-row justify-center gap-4">
          <Button asChild size="lg" className="btn-pulse bg-primary text-primary-foreground hover:bg-primary-hover font-bold px-10 py-6 h-auto rounded-2xl shadow-lg">
            <a href={BOOKING_URL}>Reservar Ahora en San José</a>
          </Button>
          <Button asChild size="lg" variant="outline" className="border-2 border-primary text-primary hover:bg-primary/5 hover:text-primary font-bold px-10 py-6 h-auto rounded-2xl">
            <a href={BOOKING_URL}>Ver Mi Precio al Instante</a>
          </Button>
        </div>
      </div>
    </section>
  );
};

const SanJoseSpanishPage = () => {
  return (
    <LocationContext.Provider value={{ slug: "san-jose", name: "San José", type: "city", regionSlug: "santa-clara" }}>
      <SEO
        title="Limpieza del Hogar en San José, CA — Uplevel Cleaning"
        description="Servicio profesional de limpieza del hogar en San José, Santa Clara, Sunnyvale, Palo Alto y más. Precios planos, reserva en 60 segundos, garantía del 200%."
        keywords="limpieza del hogar san jose, servicio de limpieza san jose ca, limpieza profunda san jose, empleada doméstica san jose, maid service silicon valley español, limpieza de casas santa clara, limpieza sunnyvale palo alto"
        url="https://uplevelcleaning.com/es/san-jose"
        locationName="San José"
        regionSlug="santa-clara"
        lang="es"
        includeLocalBusiness={true}
        includeFAQ={true}
        breadcrumbs={[
          { name: "Inicio", url: "/" },
          { name: "En Español", url: "/es/sacramento" },
          { name: "San José", url: "/es/san-jose" }
        ]}
        reviews={[
          { author: "Elena M.", rating: 5, reviewBody: "Trabajo en tecnología y no tengo tiempo para limpiar. Uplevel Cleaning es exactamente lo que necesitaba — rápido, confiable y con precios claros.", datePublished: "2026-03-20" },
          { author: "Carlos V.", rating: 5, reviewBody: "Contraté el servicio quincenal y ha sido una de las mejores decisiones. El equipo es muy profesional y siempre puntuales.", datePublished: "2026-02-14" },
          { author: "Sonia R.", rating: 5, reviewBody: "Vivo cerca de Willow Glen y estaba buscando un servicio de limpieza de confianza. ¡Uplevel superó todas mis expectativas!", datePublished: "2026-01-28" }
        ]}
      />
      <div className="flex min-h-screen flex-col" lang="es">
        <StickyBanner />
        <Navbar />
        <main className="flex-1">
          <SpanishHero cityName="San José" regionSlug="santa-clara" />

          {/* Local trust bar */}
          <div className="bg-primary/5 border-b border-primary/10 py-4">
            <div className="container flex flex-wrap justify-center gap-6 text-sm font-medium text-foreground">
              {[
                "Servicio Local en San José",
                "Silicon Valley y Alrededores",
                "Garantía del 200%",
                "Precios Transparentes",
                "Reserva en 60 Segundos",
              ].map((item, i) => (
                <span key={i} className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-primary" /> {item}
                </span>
              ))}
            </div>
          </div>

          <SpanishWhyUs cityName="San José" />
          <NearbyAreas />
          <SpanishServices />

          <FrequencyDiscounts />
          <PricingCalculator />
          <SpanishTestimonials region="san-jose" />
          <Guarantee />
          <SpanishFAQ cityName="San José" />
          <InternalLinks />
          <SpanishFinalCTA cityName="San José" />
        </main>
        <Footer />
      </div>
    </LocationContext.Provider>
  );
};

export default SanJoseSpanishPage;
